import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Main as Container } from 'Components/Main';
import { PageHelper } from '../../Components/PageHelper';
import { deleteOffice, getOfficesPaged } from 'services/offices-service';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';
import { Dialog, TextField, PageHeader } from 'Components/ui';

// ─── Icons ────────────────────────────────────────────────────────────────────

const InfoSvg = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--vo-celeste)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const SearchSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const EyeSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const EditSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const TrashSvg = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);

const PlusSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

// ─── Styled components ────────────────────────────────────────────────────────

const PageWrap = styled.div`
  padding-top: 16px;
  padding-bottom: 40px;
`;

const AddBtn = styled.button`
  height: 44px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 14px;
  font-weight: 600;
  padding: 0 20px;
  cursor: pointer;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: background 0.15s;

  &:hover { background: var(--vo-cobalto); }
`;

const InfoBanner = styled.div`
  background: white;
  border-radius: 14px;
  padding: 16px 20px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  border-left: 4px solid var(--vo-celeste);
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
`;

const InfoTextWrap = styled.div``;

const InfoTitle = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: var(--vo-ink);
  margin: 0 0 2px;
`;

const InfoDesc = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  margin: 0;
  line-height: 1.55;
`;

const FilterBar = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
  display: flex;
  gap: 14px;
  align-items: flex-end;
`;

const FilterField = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

const FilterLabel = styled.label`
  font-family: 'Maven Pro', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: var(--vo-slate);
`;


const FilterBtn = styled.button`
  height: 40px;
  background: var(--vo-celeste);
  color: white;
  border: none;
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 7px;
  transition: background 0.15s;

  &:hover { background: var(--vo-cobalto); }
`;

const TableCard = styled.div`
  background: white;
  border-radius: 14px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
`;

const Thead = styled.thead`
  background: var(--vo-mist);
`;

const Th = styled.th`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: var(--vo-slate);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 12px 16px;
  text-align: ${({ $center }) => $center ? 'center' : 'left'};
  border-bottom: 1px solid var(--vo-cloud);
  width: ${({ $width }) => $width ?? 'auto'};
`;

const Tr = styled.tr`
  border-bottom: 1px solid var(--vo-mist);
  transition: background 0.1s;

  &:hover { background: var(--vo-capri-25); }
  &:last-child { border-bottom: none; }
`;

const TdName = styled.td`
  padding: 13px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-ink);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TdMuted = styled.td`
  padding: 13px 16px;
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: var(--vo-slate);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TdActions = styled.td`
  padding: 13px 16px;
  text-align: center;
`;

const ActionsGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

const IconBtn = styled.button`
  width: 30px;
  height: 30px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--vo-slate);
  transition: background 0.15s, color 0.15s;

  &:hover {
    background: ${({ $danger }) => $danger ? 'var(--vo-danger-10)' : 'var(--vo-info-10)'};
    color: ${({ $danger }) => $danger ? 'var(--vo-danger)' : 'var(--vo-celeste)'};
  }
`;

const EmptyCell = styled.td`
  padding: 40px 16px;
  text-align: center;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-slate);
`;

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonBlock = styled.div`
  background: linear-gradient(90deg, var(--vo-mist) 25%, var(--vo-cloud) 50%, var(--vo-mist) 75%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.4s infinite linear;
  border-radius: 14px;
`;

// ─── Component ────────────────────────────────────────────────────────────────

export const Offices = () => {
  const { t } = useTranslation(['company']);
  const navigate = useNavigate();
  const selectedTenant = useSelector(useAuthTenant);

  const [nameFilter, setNameFilter] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [reFetch, setRefetch] = useState(false);
  const [page] = useState(1);

  const { isLoading, error, data: pagedOffices } = useQuery(
    ['getOfficesPaged', reFetch, page],
    async () => {
      const result = await getOfficesPaged(selectedTenant.slug, page, 15);
      if (result?.data?.data?.length > 0) return result.data;
      return { data: [], page, totalPages: 0 };
    }
  );

  const filteredData = (pagedOffices?.data ?? []).filter(o =>
    !nameFilter || o.name?.toLowerCase().includes(nameFilter.toLowerCase())
  );

  const onDelete = async (office) => {
    const confirmed = await Dialog.confirm({
      title: t('company:offices.delete.confirm', { name: office.name }),
      confirmLabel: 'Excluir',
      cancelLabel: 'Cancelar',
      confirmColor: 'var(--vo-danger)',
    });
    if (!confirmed) return;
    try {
      const res = await deleteOffice(office._id);
      if (res.status === 204) {
        setRefetch(v => !v);
        Dialog.alert({
          title: t('company:offices.delete.successTitle'),
          message: t('company:offices.delete.successMessage'),
          icon: 'success',
        });
      } else throw new Error();
    } catch {
      Dialog.alert({ title: t('company:offices.delete.errorTitle'), icon: 'error' });
    }
  };

  const tourSteps = [
    { target: 'body', content: 'Bem-vindo à página de Escritórios! Aqui você gerencia todas as localidades físicas da sua empresa.', placement: 'center', disableBeacon: true },
    { target: '[data-tour="info-section"]', content: 'Esta seção explica o objetivo da página.', placement: 'bottom', title: 'Informações' },
    { target: '[data-tour="add-button"]', content: 'Clique aqui para adicionar um novo escritório.', placement: 'bottom', title: 'Adicionar Escritório' },
    { target: '[data-tour="offices-table"]', content: 'Aqui são listados todos os escritórios cadastrados.', placement: 'top', title: 'Lista de Escritórios' },
  ];

  return (
    <Container>
      <PageHelper steps={tourSteps} tourKey="offices-tour" autoStart={true} showButton={true} />
      <PageWrap>

        {/* ── Header ─────────────────────────────────────────────── */}
        <PageHeader
          data-tour="page-title"
          title={t('company:offices.title')}
          segments={[
            { label: t('common:breadcrumbs.manage') },
            { label: t('company:offices.title') },
          ]}
          actions={
            <AddBtn data-tour="add-button" onClick={() => navigate('/offices/add')}>
              <PlusSvg />
              {t('company:offices.addNew')}
            </AddBtn>
          }
        />

        {/* ── Info banner ────────────────────────────────────────── */}
        <InfoBanner data-tour="info-section">
          <InfoSvg />
          <InfoTextWrap>
            <InfoTitle>{t('company:offices.info.title')}</InfoTitle>
            <InfoDesc>{t('company:offices.info.description')}</InfoDesc>
          </InfoTextWrap>
        </InfoBanner>

        {/* ── Filter bar ─────────────────────────────────────────── */}
        <FilterBar>
          <FilterField>
            <FilterLabel>{t('company:offices.table.name')}</FilterLabel>
            <TextField
              fullWidth
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              placeholder={t('company:offices.filter.namePlaceholder')}
              onKeyDown={e => e.key === 'Enter' && setNameFilter(nameInput)}
            />
          </FilterField>
          <FilterBtn onClick={() => setNameFilter(nameInput)}>
            <SearchSvg />
            {t('company:offices.filter.search')}
          </FilterBtn>
        </FilterBar>

        {/* ── Loading ────────────────────────────────────────────── */}
        {isLoading && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <SkeletonBlock style={{ height: '48px' }} />
            <SkeletonBlock style={{ height: '260px' }} />
          </div>
        )}

        {/* ── Error ──────────────────────────────────────────────── */}
        {error && <InfoDesc style={{ color: 'var(--vo-danger)' }}>{t('company:offices.error')}</InfoDesc>}

        {/* ── Table ──────────────────────────────────────────────── */}
        {!isLoading && !error && (
          <TableCard data-tour="offices-table">
            <Table>
              <Thead>
                <tr>
                  <Th $width="25%">{t('company:offices.table.name')}</Th>
                  <Th $width="40%">{t('company:offices.table.address')}</Th>
                  <Th $width="20%">{t('company:offices.table.phone')}</Th>
                  <Th $width="15%" $center>{t('company:offices.table.actions')}</Th>
                </tr>
              </Thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <EmptyCell colSpan={4}>{t('company:offices.noData')}</EmptyCell>
                  </tr>
                ) : (
                  filteredData.map(o => (
                    <Tr key={o._id}>
                      <TdName>{o.name}</TdName>
                      <TdMuted>{o.address || '—'}</TdMuted>
                      <TdMuted>{o.phone || '—'}</TdMuted>
                      <TdActions>
                        <ActionsGroup>
                          <IconBtn title="Visualizar" onClick={() => navigate(`/offices/show/${o._id}`)}>
                            <EyeSvg />
                          </IconBtn>
                          <IconBtn title="Editar" onClick={() => navigate(`/offices/edit/${o._id}`)}>
                            <EditSvg />
                          </IconBtn>
                          <IconBtn $danger title="Excluir" onClick={() => onDelete(o)}>
                            <TrashSvg />
                          </IconBtn>
                        </ActionsGroup>
                      </TdActions>
                    </Tr>
                  ))
                )}
              </tbody>
            </Table>
          </TableCard>
        )}

      </PageWrap>
    </Container>
  );
};
